import React, { createContext, useState, useContext, useEffect } from 'react';
import enTranslations from '../locales/en.json';
import ruTranslations from '../locales/ru.json';
import thTranslations from '../locales/th.json';
import zhTranslations from '../locales/zh.json';

// Create context
export const I18nContext = createContext({
  language: 'en',
  setLanguage: () => {},
  t: (key) => key,
});

// Available languages
const languages = {
  en: enTranslations,
  ru: ruTranslations,
  th: thTranslations,
  zh: zhTranslations,
};

// Provider component
export const I18nProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem('language');
    return savedLanguage || 'en'; // Default to English
  });

  // Save language to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  // Translation function
  const t = (key) => {
    // Split the key by dots to navigate through the nested objects
    const keys = key.split('.');
    let value = languages[language];

    // Navigate through the nested objects
    for (const k of keys) {
      if (value && value[k]) {
        value = value[k];
      } else {
        // If the key doesn't exist in the current language, try English
        if (language !== 'en') {
          let enValue = languages['en'];
          for (const k of keys) {
            if (enValue && enValue[k]) {
              enValue = enValue[k];
            } else {
              return key; // Return the key if not found in English either
            }
          }
          return enValue;
        }
        return key; // Return the key if not found
      }
    }

    return value;
  };

  // Context value
  const value = {
    language,
    setLanguage,
    t,
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

// Custom hook to use the I18n context
export const useI18n = () => {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

export default I18nProvider;
